import React, { useContext, useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { LoggedInUserContext } from './AdminPageContainer';
import { Roles } from 'src/Constants/BorgEnums';
import { getOrganization } from 'src/Services/BorgAPIClient';
import { colorAuriaYellow } from 'src/GlobalStyles/MuiTheme';
import moment from 'moment';

type OrganizationForBanner = {
  id: string;
  name: string;
  trialEndDate?: string | null;
};

const OrgAdminTrialBanner: React.FC = () => {
  const loggedInUser = useContext(LoggedInUserContext)?.loggedInUser;
  const [organization, setOrganization] = useState<OrganizationForBanner | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!loggedInUser?.organizationId || loggedInUser?.roles?.[0] !== Roles.OrganizationAdmin) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchOrg = async () => {
      try {
        const res = await getOrganization(loggedInUser.organizationId);
        const data = res?.data as OrganizationForBanner | undefined;
        if (!cancelled && data) setOrganization(data);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOrg();
    return () => { cancelled = true; };
  }, [loggedInUser?.organizationId, loggedInUser?.roles]);

  if (loading || error || !organization) return null;
  if (loggedInUser?.roles?.[0] !== Roles.OrganizationAdmin) return null;
  if (!organization.trialEndDate) return null;

  const endDate = new Date(organization.trialEndDate);
  if (endDate <= new Date()) return null;

  const now = moment();
  const end = moment(endDate);
  const daysLeft = end.diff(now, 'days');
  const timeLeftText = daysLeft >= 30
    ? `${Math.round(daysLeft / 30)} month${Math.round(daysLeft / 30) !== 1 ? 's' : ''}`
    : `${daysLeft} day${daysLeft !== 1 ? 's' : ''}`;
  const dateFormatted = end.format('MM/DD/YYYY');

  return (
    <Typography
      component="span"
      variant="subtitle1"
      sx={{ color: colorAuriaYellow, flexShrink: 0 }}
    >
      The trial for {organization.name} will be ending in {timeLeftText} on {dateFormatted}.
    </Typography>
  );
};

export default OrgAdminTrialBanner;
